import { HttpHeaders } from '@angular/common/http';
import * as i0 from '@angular/core';
import { Inject, Injectable } from '@angular/core';
import { genericRetryStrategy } from '@upr-web-primer/app-utils';
import * as i1 from '@upr-web-primer/dal';
import { TokenStatus, DalService } from '@upr-web-primer/dal';
import * as i3 from '@upr-web-primer/ms-guests';
import { MsGuestsService } from '@upr-web-primer/ms-guests';
import * as i4 from '@upr-web-primer/notification';
import * as i5 from '@upr-web-primer/page-labels';
import * as i2 from '@upr-web-primer/storage';
import { LocalStorageService } from '@upr-web-primer/storage';
import { WINDOW } from '@upr-web-primer/window';
import { isEmpty, get, forEach, isArray, find, map, flow, values, flatten, sortBy, first, some, includes, reduce, concat, uniq, join, filter as filter$1 } from 'lodash-es';
import { BehaviorSubject, Subscription, filter, take, skip, firstValueFrom, from, retry, delay, switchMap, of, catchError } from 'rxjs';
import { __decorate, __param, __metadata } from 'tslib';
import { CardTypes } from '@upr-web-primer/card-types';
import * as i5$1 from '@upr-web-primer/spinner';
import * as i4$1 from 'cachefactory';
import moment from 'moment';
let BaseCartService = class BaseCartService {
  constructor(defaultValues, dal, localStorage, guest) {
    this.defaultValues = defaultValues;
    this.dal = dal;
    this.localStorage = localStorage;
    this.guest = guest;
  }
  replaceStoreId(url) {
    return url.replace('{storeId}', this.localStorage.getItem('STORE_ID') || this.defaultValues.STORE_ID);
  }
  replaceContractId(url) {
    return url.replace('{contract_id}', this.localStorage.getItem('CONTRACT_ID') || this.defaultValues.CONTRACT_ID);
  }
  replaceDoCalculate(url, calculate = false) {
    return url.replace('{do_calculate}', calculate ? 'Y' : 'N');
  }
  async httpRequest(relativeUrl, type, body, headers, timeoutDelay, isRetry, getResponseHeaders, gatewayUrl) {
    const tokens = await this.guest.account.getAuthnHeaders();
    if (isEmpty(headers)) {
      headers = new HttpHeaders();
    }
    if (headers instanceof HttpHeaders) {
      headers = headers.set('WCToken', tokens.WCToken);
      headers = headers.set('WCTrustedToken', tokens.WCTrustedToken);
    }
    relativeUrl = this.replaceStoreId(relativeUrl);
    relativeUrl = this.replaceContractId(relativeUrl);
    return this.dal.httpRequest(relativeUrl, type, body, headers, timeoutDelay, isRetry, getResponseHeaders, gatewayUrl).catch(error => {
      const errorMessage = get(error, 'error.errors[0].errorKey');
      const errorMessage2 = get(error, 'error["Error message"]');
      if (errorMessage2 || error.status === 401 && errorMessage === '_ERR_INVALID_COOKIE') {
        this.dal.updateAppToken(TokenStatus.Reset_Authn);
        return this.httpRequest(relativeUrl, type, body, headers, timeoutDelay, isRetry, getResponseHeaders, gatewayUrl);
      }
      throw error;
    });
  }
};
BaseCartService = __decorate([__param(0, Inject('DefaultValues')), __metadata("design:paramtypes", [Object, DalService, LocalStorageService, MsGuestsService])], BaseCartService);
const relativeUrls = {
  ADD_FOLIO_CARD: '/payment/wallet',
  CARDINAL_TOKEN: '/cardinal/token',
  PAYMENT_SUBMIT: '/payment/card',
  PAY_ADD_FOLIO_CARD: '/payment/wallet',
  PAY_PAYMENT_SUBMIT: '/payment/card',
  PAYMENT_INSTANCE_SUBMIT: '/instances/{instance-id}/commerce-checkout-instant-card',
  PAYMENT_GUEST_SUBMIT: '/guests/{guest-id}/commerce-checkout-instant-card',
  ADD_TO_CART: '/shop/wcs/resources/store/{storeId}/cart',
  UPDATE_TICKET: '/shop/wcs/resources/store/{storeId}/cart/@self/update_order_item',
  DELETE_TICKET: '/shop/wcs/resources/store/{storeId}/cart/@self/delete_order_item',
  DELIVERY_METHODS: '/shop/wcs/resources/store/{storeId}/cart/eligible_ship_modes',
  PROMOTION_CODE: '/shop/wcs/resources/store/{storeId}/cart/@self/assigned_promotion_code',
  SET_CART_SHIPPING_INFO: '/shop/wcs/resources/store/{storeId}/cart/@self/shipping_info',
  GET_FLEXPAY_CONTRACT: '/flexpay/Flexpay/retrieveContract',
  CART_DETAILS: '/shop/wcs/resources/store/{storeId}/cart/byGroupMethod/ice_v2?doCalculate={do_calculate}&contractId={contract_id}'
};
class CartService extends BaseCartService {
  constructor(window, defaultValues, dal, localStorage, guest, sessionStorage, notification, pageLabelsResolver) {
    super(defaultValues, dal, localStorage, guest);
    this.window = window;
    this.defaultValues = defaultValues;
    this.dal = dal;
    this.localStorage = localStorage;
    this.guest = guest;
    this.sessionStorage = sessionStorage;
    this.notification = notification;
    this.pageLabelsResolver = pageLabelsResolver;
    this.orderDetails = null;
    this.adobeTagsParam = {
      adobeTags: []
    };
    this.ticketsQuantity = 0;
    this.hasFloridaTickets = false;
    this.hasGeorgiaTickets = false;
    this.totalProductPrice = 0;
    this.totalPriceSavings = 0;
    this.maxQuantity = {
      parkTickets: 0,
      annualPasses: 0,
      expressPasses: 0,
      addOns: 0
    };
    this.totalAssignmentQuantity = {
      parkTickets: 0,
      annualPasses: 0,
      expressPasses: 0,
      addOns: 0
    };
    this.isFlexPay = false;
    this.orderItemIdsForAll = [];
    this.ticketProperties = {
      parkTickets: {
        pathToAdultTickets: 'adult',
        pathToChildTickets: 'child'
      },
      expressPasses: {},
      annualPasses: {
        pathToAdultTickets: 'adult',
        pathToChildTickets: 'child'
      },
      addOns: {
        pathToAdultTickets: 'adult',
        pathToChildTickets: 'child',
        pathToTicketsForAll: 'all'
      }
    };
    this.ages = ['Adult', 'Child', 'All'];
    this.inventoryIssues = ['_ERR_INV_ALLOCATION', '_ERR_INVENTORY_MISSING_DATA', '_ERR_INVENTORY_HOLD_NOT_SUCCESSFULL', '_ERR_INVALID_TICKET_DATE'];
    this.retryCount = 0;
    this.toggleSavingsBanner = [];
    this.cartLoaded$ = new BehaviorSubject(false);
    this.subscriptions$ = new Subscription();
    this.defaultValues.ADDONS = this.defaultValues.ADDONS || 'addOns';
    this.defaultValues.EXPRESS_PASSES = this.defaultValues.EXPRESS_PASSES || 'expressPasses';
    this.defaultValues.ANNUAL_PASSES = this.defaultValues.ANNUAL_PASSES || 'annualPasses';
    this.defaultValues.PARK_TICKETS = this.defaultValues.PARK_TICKETS || 'parkTickets';
    this.pageLabelsResolver.resolve().then(data => {
      this.pageLabels = data;
    });
    this.guest.helper.instanceLoaded$.pipe(filter(bool => bool === true), take(1)).subscribe(() => {
      const isSignOff = this.window.location.search.includes('signoff=true');
      if (!isSignOff && !this.guest.helper.isAuth()) {
        this.loadCart();
      }
    });
    const loggedInCart$ = this.guest.helper.loggedInStatus$.pipe(skip(1)).subscribe(() => {
      // Clear authn so cart call is with new tokens
      this.dal.updateAppToken(TokenStatus.Reset_Authn);
      this.loadCart();
    });
    this.subscriptions$.add(loggedInCart$);
  }
  loadCart() {
    if (this.guest.helper.envConfig.cart_enabled) {
      return this.miniCart().then(() => this.init());
    }
  }
  miniCart() {
    return this.loadCartDetails().then(orderDetails => {
      this.orderDetails = orderDetails;
      this.ticketsQuantity = 0;
      this.totalProductPrice = 0;
      this.totalPriceSavings = 0;
      forEach(orderDetails.orderItemGroups, (ticketsPurchasedForCurrentType, ticketType) => {
        let maxQuantityPurchased = {
          forAdult: 0,
          forChild: 0,
          forAll: 0
        };
        forEach(ticketsPurchasedForCurrentType, ticket => {
          const refinedTicket = {
            adultQuantity: 0,
            childQuantity: 0,
            allQuantity: 0
          };
          if (isArray(ticket)) {
            forEach(ticket, item => {
              refinedTicket.adultQuantity += Number(get(item, 'adult.quantity', 0));
              refinedTicket.childQuantity += Number(get(item, 'child.quantity', 0));
              refinedTicket.allQuantity += Number(get(item, 'quantity', 0)) || Number(get(item, 'all.quantity', 0));
              this.totalPriceSavings += Number(get(item, 'adult.totalPriceSavings', 0)) + Number(get(item, 'child.totalPriceSavings', 0)) + Number(get(item, 'totalPriceSavings', 0));
            });
          } else {
            refinedTicket.adultQuantity = Number(get(ticket, 'adult.quantity', 0));
            refinedTicket.childQuantity = Number(get(ticket, 'child.quantity', 0));
            refinedTicket.allQuantity = Number(get(ticket, 'quantity', 0)) || Number(get(ticket, 'all.quantity', 0));
            this.totalPriceSavings += Number(get(ticket, 'adult.totalPriceSavings', 0)) + Number(get(ticket, 'child.totalPriceSavings', 0)) + Number(get(ticket, 'totalPriceSavings', 0));
          }
          maxQuantityPurchased = {
            forAdult: refinedTicket.adultQuantity > maxQuantityPurchased.forAdult ? refinedTicket.adultQuantity : maxQuantityPurchased.forAdult,
            forChild: refinedTicket.childQuantity > maxQuantityPurchased.forChild ? refinedTicket.childQuantity : maxQuantityPurchased.forChild,
            forAll: refinedTicket.allQuantity > maxQuantityPurchased.forAll ? refinedTicket.allQuantity : maxQuantityPurchased.forAll
          };
          this.ticketsQuantity += refinedTicket.adultQuantity + refinedTicket.childQuantity + refinedTicket.allQuantity;
        });
        this.maxQuantity[ticketType] = maxQuantityPurchased;
      });
      this.localStorage.setItem('CART_ITEM_COUNT', String(this.ticketsQuantity));
      this.sessionStorage.setItem('CART_ITEM_COUNT', String(this.ticketsQuantity));
      this.totalProductPrice = get(this.orderDetails, 'pricing.totalProductPrice', 0);
      this.sessionStorage.setItem('CART_TOTAL_PRICE', String(this.totalProductPrice));
      this.sessionStorage.setItem('CART_SAVINGS', String(this.totalPriceSavings));
      return {
        itemQuantity: this.ticketsQuantity,
        totalProductPrice: this.totalProductPrice,
        totalPriceSavings: this.totalPriceSavings
      };
    }).catch(error => {
      this.ticketsQuantity = 0;
      this.localStorage.setItem('CART_ITEM_COUNT', '0');
      throw error;
    });
  }
  // add to cart
  addItems(items, ticketType) {
    const nonFlexPaySalesChannel = {
      value: this.defaultValues.NONFLEXPAYSALESCHANNELID,
      name: 'SALES_CHANNEL_ID'
    };
    const flexPaySalesChannel = {
      value: this.defaultValues.FLEXPAYSALESCHANNELID,
      name: 'SALES_CHANNEL_ID'
    };
    forEach(items.orderItem, item => {
      if (item.attributes) {
        const salesChannelIdExist = find(item.attributes, {
          name: 'SALES_CHANNEL_ID'
        });
        if (!salesChannelIdExist) {
          const flexPayAttr = find(item.attributes, {
            name: 'FLEXPAY',
            value: 'Y'
          });
          item.attributes.push(flexPayAttr ? flexPaySalesChannel : nonFlexPaySalesChannel);
        }
      } else {
        item.attributes = [].concat(nonFlexPaySalesChannel);
      }
    });
    return this.httpRequest(relativeUrls.ADD_TO_CART, 'POST', items).then(() => this.loadCart()).catch(errorResponse => {
      if (errorResponse.status === 500 || !errorResponse) {
        items.errorInResponse = get(this.pageLabels, 'ER65', '');
      } else {
        items.errorInResponse = this.handleCartErrors(get(errorResponse, 'error.errors', errorResponse), ticketType);
      }
      throw errorResponse;
    });
  }
  updateItems(items, shouldLoadCart = true) {
    const retryStr = genericRetryStrategy();
    return firstValueFrom(from(this.httpRequest(relativeUrls.UPDATE_TICKET, 'PUT', items)).pipe(retry(retryStr), delay(100),
    // add delay to allow commerce a slight bit of time to catch up, as sometimes we are just slightly too quick.
    switchMap(() => shouldLoadCart ? this.loadCart() : of(true)),
    // Optional toggle to skip loadCart() after update (e.g., FlexPay consent signature) to avoid unnecessary commerce API calls.
    catchError(errorResponse => {
      this.handleCartErrors(get(errorResponse, 'error.errors', errorResponse));
      throw errorResponse;
    })));
  }
  loadDeliveryMethodsDetails() {
    const headers = this.noCacheHeaders();
    return this.httpRequest(relativeUrls.DELIVERY_METHODS, 'GET', null, headers).then(response => {
      if (!response.deliveryOptions) {
        return false;
      }
      return response.deliveryOptions;
    }).catch(error => {
      throw error;
    });
  }
  loadPromotionCode(promoCode) {
    const request = {
      promoCode: promoCode
    };
    return this.httpRequest(relativeUrls.PROMOTION_CODE, 'POST', request).then(() => this.loadCart()).catch(errorResponse => {
      throw errorResponse;
    });
  }
  deletePromotionCode(promoCode) {
    return this.httpRequest(relativeUrls.PROMOTION_CODE + '/' + promoCode, 'DELETE', null).then(() => this.loadCart()).catch(errorResponse => {
      throw errorResponse;
    });
  }
  getQuantity(assignment) {
    let qty = 0;
    forEach(this.tickets, ticketsGroup => {
      forEach(ticketsGroup, ticket => {
        qty += ticket.adultQuantity + ticket.allQuantity + ticket.childQuantity;
      });
    });
    if (assignment && this.ticketsQuantity >= assignment && qty === 0) {
      this.ticketsQuantity -= assignment;
    } else {
      this.ticketsQuantity = qty;
    }
    return this.ticketsQuantity;
  }
  decreaseTicketsQty() {
    return this.ticketsQuantity--;
  }
  increaseTicketsQty() {
    return this.ticketsQuantity++;
  }
  updateCartShippingAddress(addressId) {
    if (!get(this, 'orderItemIdsForAll.length')) {
      return Promise.resolve().then(() => false);
    }
    const orderItems = map(this.orderItemIdsForAll, itemId => {
      return {
        orderItemId: itemId,
        addressId: addressId
      };
    });
    return this.httpRequest(relativeUrls.SET_CART_SHIPPING_INFO, 'PUT', {
      orderItem: orderItems
    });
  }
  async normalizeDeliveryMethods(deliveryId) {
    try {
      const updatedItems = this.getItemsToUpdateDeliveryMethod(deliveryId);
      if (updatedItems.orderItem.length === 0) {
        return true;
      }
      return this.updateItems(updatedItems);
    } catch (error) {
      throw false;
    }
  }
  getOldestOrderItemDeliveryMethodId(eligibleDeliveryMethods) {
    const orders = flow([values, flatten])(this.tickets);
    const eligibleItems = this.itemsWithValidDeliveryMethod(orders, eligibleDeliveryMethods);
    let oldestItem = sortBy(eligibleItems, eligibleItem => this.getCreateDate(eligibleItem));
    oldestItem = first(oldestItem);
    return this.getTicketDeliveryMethodId(oldestItem);
  }
  getDisplayPricing() {
    return this.displayPricing;
  }
  showFlexPay() {
    return this.isFlexPay;
  }
  hasInventoryIssue(messages) {
    return some(messages, message => {
      return includes(this.inventoryIssues, message.key);
    });
  }
  flexPayContract() {
    if (!this.orderDetails || !this.orderDetails.orderId) {
      return;
    }
    const request = {
      orderId: this.orderDetails.orderId
    };
    return this.httpRequest(relativeUrls.GET_FLEXPAY_CONTRACT, 'POST', request);
  }
  setDataAnalytics(postalCode, confirmationID) {
    const dataAnalytics = {};
    const productIds = [];
    const productData = [];
    let data = '';
    let typeTicket = '';
    const quantityGuest = {};
    quantityGuest.adult = 0;
    quantityGuest.child = 0;
    quantityGuest.allAge = 0;
    quantityGuest.quantity = 0;
    forEach(this.tickets, (ticket, ticketType) => {
      quantityGuest.quantity += ticket.length;
      forEach(ticket, item => {
        quantityGuest.adult += item.adultQuantity;
        quantityGuest.child += item.childQuantity;
        quantityGuest.allAge += item.allQuantity;
        switch (ticketType) {
          case this.defaultValues.PARK_TICKETS:
            typeTicket = 'Ticket;';
            break;
          case this.defaultValues.ADDONS:
            typeTicket = (item.adultQuantity > 0 || item?.ticketsForAdult?.quantity > 0 ? item.ticketsForAdult.item.attributeMap.ITEM_TYPE : item.childQuantity > 0 || item?.ticketsForChild?.quantity > 0 ? item.ticketsForChild.item.attributeMap.ITEM_TYPE : item?.ticketsForAll?.item.attributeMap.ITEM_TYPE) + ';';
            break;
          case this.defaultValues.EXPRESS_PASSES:
          case this.defaultValues.ANNUAL_PASSES:
            typeTicket = 'Pass;';
            break;
        }
        if (item.adultQuantity > 0) {
          const adultItemPrice = item.isFlexPay ? item.ticketsForAdult.unitPrice : item.ticketsForAdult.totalPrice;
          data = typeTicket + item.ticketsForAdult.item.partNumber + ';' + item.adultQuantity + ';' + adultItemPrice + ';event41=' + adultItemPrice + '|event43=' + item.adultQuantity;
          productData.push(data);
          productIds.push(item.ticketsForAdult.item.partNumber);
        }
        if (item.childQuantity > 0) {
          const childItemPrice = item.isFlexPay ? item.ticketsForChild.unitPrice : item.ticketsForChild.totalPrice;
          data = typeTicket + item.ticketsForChild.item.partNumber + ';' + item.childQuantity + ';' + childItemPrice + ';event41=' + childItemPrice + '|event43=' + item.childQuantity;
          productData.push(data);
          productIds.push(item.ticketsForChild.item.partNumber);
        }
        if (item.allQuantity > 0) {
          data = typeTicket + item.ticketsForAll.item.partNumber + ';' + item.allQuantity + ';' + item.ticketsForAll.totalPrice + ';event41=' + item.ticketsForAll.totalPrice + '|event43=' + item.allQuantity;
          productData.push(data);
          productIds.push(item.ticketsForAll.item.partNumber);
        }
      });
    });
    productData.push('event42=1');
    quantityGuest.totalGuests = quantityGuest.adult + quantityGuest.child + quantityGuest.allAge;
    dataAnalytics.quantityGuest = quantityGuest;
    dataAnalytics.data = productData || [];
    dataAnalytics.productIds = productIds || [];
    this.dataAnalytics = {
      confirmationID: confirmationID,
      zipcode: postalCode,
      creditCardType: '',
      promoCode: '',
      deliveryMethod: get(this.orderDetails, 'shipping.description', ''),
      quantity: get(dataAnalytics, 'quantityGuest.quantity'),
      totalGuests: get(dataAnalytics, 'quantityGuest.totalGuests'),
      adults: get(dataAnalytics, 'quantityGuest.adult'),
      allAges: get(dataAnalytics, 'quantityGuest.allAge'),
      children: get(dataAnalytics, 'quantityGuest.child'),
      currency: 'USD',
      product: get(dataAnalytics, 'data').join(),
      productID: get(dataAnalytics, 'productIds').join(),
      totalBeforeTax: get(this.displayPricing, 'totalProductPrice'),
      totalAfterTax: get(this.displayPricing, 'grandTotal'),
      totalAmountFinanced: get(this.displayPricing, 'totalFinanced', 0),
      totalAmountCollectedToday: get(this.displayPricing, 'totalCharged', 0)
    };
  }
  getDataAnalytics() {
    return this.dataAnalytics;
  }
  loadCartDetails() {
    return Promise.resolve().then(() => {
      const relativeUrl = this.replaceDoCalculate(relativeUrls.CART_DETAILS, this.checkForCartPage());
      const headers = this.noCacheHeaders();
      return this.httpRequest(relativeUrl, 'GET', null, headers);
    }).then(response => {
      if (response === undefined) {
        // handle a 401 error from dal service...
        // We don't return an error, so mitigation done by checking for response for now.
        throw {
          err: '401'
        };
      }
      const order = get(response, 'order', {});
      if (!order) {
        return false;
      }
      return order;
    }).catch(error => {
      throw error;
    });
  }
  noCacheHeaders() {
    let httpHeaders = new HttpHeaders();
    httpHeaders = httpHeaders.append('PRAGMA', 'NO-CACHE');
    httpHeaders = httpHeaders.append('CACHE-CONTROL', 'NO-STORE,max-age=0');
    return httpHeaders;
  }
  init(callFromShoppingCart) {
    this.displayPricing = {
      totalProductPrice: 0.0,
      totalPriceSavings: 0.0
    };
    this.ticketsQuantity = 0;
    this.tickets = {};
    this.localStorage.setItem('CART_ITEM_COUNT', '0');
    this.sessionStorage.setItem('CART_ITEM_COUNT', '0');
    if (!this.orderDetails) {
      this.cartLoaded$.next(true);
      return true;
    }
    this.hasFloridaTickets = false;
    this.hasGeorgiaTickets = false;
    if (this.orderDetails.offer !== null && get(this.orderDetails, 'offer.sku')) {
      this.orderDetails.offer.sessionId = this.orderDetails.externalUserId;
      this.offer = typeof this.orderDetails.offer === 'object' ? this.buildOffer(this.orderDetails.offer) : this.orderDetails.offer;
    }
    this.displayPricing = this.orderDetails.displayPricing;
    this.flexPayPaymentPlans = get(this.orderDetails, 'displayPricing.financeDetails[0].paymentPlans');
    this.buildTicketsGroups(this.orderDetails.orderItemGroups);
    this.reorderGroups();
    if (callFromShoppingCart) {
      this.findWarningErrorMessages(this.orderDetails.messages);
    }
    this.cartLoaded$.next(true);
    return true;
  }
  buildOffer(offer) {
    const product = {};
    const sku = {
      buyable: Boolean(get(offer.sku, 'buyable', true)),
      price: offer.sku.price ? parseFloat(offer.sku.price[0].value) : 0.0,
      name: offer.sku.name,
      uniqueID: offer.sku.uniqueID,
      partNumber: offer.sku.partNumber
    };
    forEach(offer.sku.price, priceObj => {
      const priceValue = get(priceObj, 'value');
      const priceUsage = get(priceObj, 'usage');
      if (priceUsage === 'Display' && priceValue) {
        sku.price = priceValue;
      }
      if (priceUsage === 'Offer' && priceValue) {
        sku.offerPrice = priceValue || sku.price;
      }
    });
    forEach(offer.sku.attributes, attr => {
      const skuAttrName = get(attr, 'identifier', '').toLowerCase();
      sku[skuAttrName] = attr.values;
    });
    product.name = offer.name;
    product.geolocation = offer.geolocation;
    product.sku = sku;
    product.offerAccepted = {
      sessionId: offer.sessionId,
      code: offer.code,
      treatmentCode: offer.treatmentCode
    };
    return product;
  }
  buildTicketsGroups(groupedTicketsBytype) {
    this.maxQuantity = {
      parkTickets: 0,
      annualPasses: 0,
      expressPasses: 0,
      addOns: 0
    };
    this.totalAssignmentQuantity = {
      parkTickets: 0,
      annualPasses: 0,
      expressPasses: 0,
      addOns: 0
    };
    this.tickets = {};
    this.ticketsQuantity = 0;
    this.isFlexPay = false;
    this.orderItemIdsForAll = [];
    forEach(groupedTicketsBytype, (ticketsPurchasedForCurrentType, ticketType) => {
      if (ticketType === this.defaultValues.ADDONS) {
        ticketsPurchasedForCurrentType = reduce(ticketsPurchasedForCurrentType, (result, value) => {
          return concat(result, value);
        }, []);
      }
      const ticketsPurchased = [];
      let maxQuantityPurchased = {
        forAdult: 0,
        forChild: 0,
        forAll: 0
      };
      let totalAssignmentQuantity = {
        forAdult: 0,
        forChild: 0,
        forAll: 0
      };
      forEach(ticketsPurchasedForCurrentType, ticket => {
        let refinedTicket = this.getRefinedTicket(ticketType, ticket);
        refinedTicket = this.getDemographicsRequired(refinedTicket);
        maxQuantityPurchased = {
          forAdult: refinedTicket.adultQuantity > maxQuantityPurchased.forAdult ? refinedTicket.adultQuantity : maxQuantityPurchased.forAdult,
          forChild: refinedTicket.childQuantity > maxQuantityPurchased.forChild ? refinedTicket.childQuantity : maxQuantityPurchased.forChild,
          forAll: refinedTicket.allQuantity > maxQuantityPurchased.forAll ? refinedTicket.allQuantity : maxQuantityPurchased.forAll
        };
        const prevAdult = totalAssignmentQuantity.forAdult;
        const prevChild = totalAssignmentQuantity.forChild;
        const prevForAll = totalAssignmentQuantity.forAll;
        totalAssignmentQuantity = {
          forAdult: prevAdult + (refinedTicket.adultQuantity || 0),
          forChild: prevChild + (refinedTicket.childQuantity || 0),
          forAll: prevForAll + (refinedTicket.allQuantity || 0)
        };
        this.ticketsQuantity += refinedTicket.adultQuantity + refinedTicket.childQuantity + refinedTicket.allQuantity;
        if ((ticketType === this.defaultValues.PARK_TICKETS || ticketType === this.defaultValues.ANNUAL_PASSES) && this.hasFloridaTickets === false) {
          this.hasFloridaTickets = get(refinedTicket, 'pointOfOrigin', '').indexOf('Florida') > -1;
        }
        if ((ticketType === this.defaultValues.PARK_TICKETS || ticketType === this.defaultValues.ANNUAL_PASSES) && this.hasGeorgiaTickets === false) {
          this.hasGeorgiaTickets = get(refinedTicket, 'pointOfOrigin', '').indexOf('Georgia') > -1;
        }
        if (ticketType === this.defaultValues.EXPRESS_PASSES && refinedTicket.messages) {
          this.orderDetails.messages = concat(this.orderDetails.messages, refinedTicket.messages);
        }
        if (!this.isFlexPay) {
          this.isFlexPay = refinedTicket.isFlexPay;
        }
        ticketsPurchased.push(refinedTicket);
        this.orderItemIdsForAll = concat(this.orderItemIdsForAll, refinedTicket.orderItemIdsForAll);
      });
      this.maxQuantity[ticketType] = maxQuantityPurchased;
      this.totalAssignmentQuantity[ticketType] = totalAssignmentQuantity;
      this.tickets[ticketType] = ticketsPurchased;
    });
    this.sessionStorage.setItem('CART_ITEM_COUNT', String(this.ticketsQuantity));
    this.localStorage.setItem('CART_ITEM_COUNT', String(this.ticketsQuantity));
  }
  getRefinedTicket(ticketType, ticket) {
    const ticketProps = this.ticketProperties[ticketType];
    const pathToChildTickets = ticketProps.pathToChildTickets;
    const pathToAdultTickets = ticketProps.pathToAdultTickets;
    const pathToTicketsForAll = ticketProps.pathToTicketsForAll;
    ticket.ticketsForAdult = pathToAdultTickets ? ticket[pathToAdultTickets] : {};
    ticket.ticketsForChild = pathToChildTickets ? ticket[pathToChildTickets] : {};
    ticket.ticketsForAll = pathToTicketsForAll ? ticket[pathToTicketsForAll] : {};
    if (!pathToAdultTickets && !pathToChildTickets && !pathToTicketsForAll) {
      ticket.ticketsForAll = ticket;
    }
    ticket.adultQuantity = !isEmpty(ticket.ticketsForAdult) && ticket.ticketsForAdult && ticket.ticketsForAdult.quantity ? ticket.ticketsForAdult.quantity : 0;
    ticket.childQuantity = !isEmpty(ticket.ticketsForChild) && ticket.ticketsForChild && ticket.ticketsForChild.quantity ? ticket.ticketsForChild.quantity : 0;
    ticket.allQuantity = !isEmpty(ticket.ticketsForAll) && ticket.ticketsForAll && ticket.ticketsForAll.quantity ? ticket.ticketsForAll.quantity : 0;
    ticket.isTicketForAll = ticket.allQuantity > 0;
    ticket.attributes = [];
    if (ticket.adultQuantity > 0 && ticket.ticketsForAdult) {
      ticket.isFlexPay = get(ticket, 'ticketsForAdult.orderItems[0].pricing.paymentPlan');
      ticket.attributes = this.getTicketInfo(ticket.ticketsForAdult.item);
      if (get(ticket, 'attributes.ui_widget', '').indexOf('TIER_BMG') > -1) {
        this.getSeatInfo(ticket, 'ticketsForAdult');
      }
    }
    if (ticket.childQuantity > 0 && ticket.ticketsForChild) {
      ticket.isFlexPay = get(ticket, 'ticketsForChild.orderItems[0].pricing.paymentPlan');
      const attributesBmg = {};
      if (!!get(ticket, 'attributes.selectedRows')) {
        attributesBmg.selectedRows = ticket.attributes.selectedRows;
      }
      if (!!get(ticket, 'attributes.selectedSeats')) {
        attributesBmg.selectedSeats = ticket.attributes.selectedSeats;
      }
      if (!!get(ticket, 'attributes.selectedTiers')) {
        attributesBmg.selectedTiers = ticket.attributes.selectedTiers;
      }
      ticket.attributes = this.getTicketInfo(ticket.ticketsForChild.item, attributesBmg);
      if (get(ticket, 'attributes.ui_widget', '').indexOf('TIER_BMG') > -1) {
        this.getSeatInfo(ticket, 'ticketsForChild');
      }
    }
    if (ticket.allQuantity > 0 && ticket.ticketsForAll) {
      ticket.isFlexPay = get(ticket, 'ticketsForAll.orderItems[0].pricing.paymentPlan');
      ticket.attributes = this.getTicketInfo(ticket.ticketsForAll.item);
      if (get(ticket, 'attributes.ui_widget', '').indexOf('TIER_BMG') > -1) {
        this.getSeatInfo(ticket, 'ticketsForAll');
      }
    }
    ticket.attributes.isFlexPay = Boolean(get(ticket, 'isFlexPay'));
    ticket.attributes.useDate = get(ticket, 'useDate');
    if (get(ticket, 'attributes.ui_widget', '').indexOf('TIER_BMG') > -1) {
      let selectedRows = get(ticket.attributes, 'selectedRows', '');
      selectedRows = uniq(selectedRows);
      selectedRows = join(selectedRows, ',');
      ticket.attributes.selectedRows = selectedRows;
      let selectedSeats = get(ticket.attributes, 'selectedSeats', '');
      selectedSeats = uniq(selectedSeats);
      selectedSeats = sortBy(selectedSeats);
      ticket.attributes.selectedSeats = selectedSeats;
      let selectedTiers = get(ticket.attributes, 'selectedTiers', '');
      selectedTiers = uniq(selectedTiers);
      selectedTiers = join(selectedTiers, ',');
      ticket.attributes.selectedTiers = selectedTiers;
    }
    return ticket;
  }
  getSeatInfo(ticket, type) {
    if (!ticket || !type) {
      return ticket;
    }
    if (!ticket.attributes.selectedRows) {
      ticket.attributes.selectedRows = [];
    }
    if (!ticket.attributes.selectedSeats) {
      ticket.attributes.selectedSeats = [];
    }
    if (!ticket.attributes.selectedTiers) {
      ticket.attributes.selectedTiers = [];
    }
    let selectedTier = '';
    const components = get(ticket[type], 'item.components');
    map(components, component => {
      const tierDisplayName = get(component, 'attributeMap.UI_WIZARD_DISPLAY_NAME');
      if (tierDisplayName) {
        selectedTier = get(tierDisplayName, '0', tierDisplayName);
      }
    });
    map(ticket[type].orderItems, orderItem => {
      if (get(orderItem, 'attributeMap')) {
        const attributeMapList = map(orderItem.attributeMap, value => {
          return value;
        });
        if (!selectedTier) {
          selectedTier = get(attributeMapList, '0.INV_AREA') || get(attributeMapList, '1.INV_AREA', '');
        }
        ticket.attributes.selectedTiers.push(selectedTier);
        const seatLow = parseInt(get(attributeMapList, '0.INV_SEAT_NUM_LOW') || get(attributeMapList, '1.INV_SEAT_NUM_LOW', ''), 10);
        const seatHigh = parseInt(get(attributeMapList, '0.INV_SEAT_NUM_HIGH') || get(attributeMapList, '1.INV_SEAT_NUM_HIGH', ''), 10);
        if (seatLow && seatHigh) {
          for (let seat = seatLow; seat <= seatHigh; seat++) {
            ticket.attributes.selectedSeats.push(seat);
          }
        } else if (seatLow) {
          ticket.attributes.selectedSeats.push(seatLow);
        } else if (seatHigh) {
          ticket.attributes.selectedSeats.push(seatHigh);
        }
        ticket.attributes.selectedRows.push(get(attributeMapList, '0.INV_ROW') || get(attributeMapList, '1.INV_ROW', ''));
      }
    });
    return ticket;
  }
  getTicketInfo(ticket, attributesBmg) {
    const attributes = {
      productPartNumber: ticket.name,
      productItemType: ticket.attributeMap.ITEM_TYPE[0],
      skuPartNumber: ticket.partNumber
    };
    if (!!attributesBmg) {
      forEach(attributesBmg, (value, key) => {
        if (key && value) {
          attributes[key] = value;
        }
      });
    }
    forEach(ticket.attributeMap, (value, key) => {
      attributes[key.toLowerCase()] = value[0];
    });
    map(ticket.components, component => {
      if (get(component, 'attributeMap.INVENTORY_CONTROLLED.0') === 'YES') {
        attributes.comboParkNum = get(component, 'attributeMap.PARK_NUM.0');
      }
    });
    return attributes;
  }
  getDemographicsRequired(ticket) {
    forEach(this.ages, age => {
      if (ticket[age.toLowerCase() + 'Quantity'] > 0) {
        const ageList = get(ticket, 'ticketsFor' + age + '.item.attributes', []);
        const dobRequired = ageList.filter(attr => {
          return attr.name === 'ADDITIONAL_GUEST_DEMOGRAPHICS_REQUIRED' && attr.value.includes('DOB');
        });
        ticket.dobRequired = ticket.dobRequired || dobRequired.length > 0;
      }
    });
    return ticket;
  }
  handleCartErrors(errors, ticketType) {
    let errorInvForAddon = '';
    let errorMessage = '';
    forEach(errors, error => {
      errorMessage = error.errorMessage;
      if (error.errorKey && ticketType) {
        if (ticketType.isTicket) {
          if (error.errorKey === '_ERR_UPC_CODE_INVALID') {
            errorInvForAddon = errorMessage;
          }
        } else if (ticketType.isExpress) {
          if (error.errorKey === '_ERR_INVENTORY_HOLD_NOT_SUCCESSFULL') {
            errorMessage = this.pageLabels.ER50;
          }
        } else if (ticketType.isAddons) {
          if (error.errorKey === '_ERR_INVENTORY_HOLD_NOT_SUCCESSFULL' || error.errorKey === '_ERR_INVENTORY_RESPONSE_INVALID') {
            errorInvForAddon = this.pageLabels.ER57;
            errorMessage = this.pageLabels.ER57;
          }
        }
      }
      if (error.errorKey === '_ERR_INVENTORY_HOLD_NOT_SUCCESSFULL' && error.errorMessage === errorMessage) {
        errorMessage = this.pageLabels.ER50;
      }
      if (error.errorKey !== '_ERR_UPC_CODE_INVALID') {
        if (get(error, 'errorKey') === '_ERR_PROD_NOT_ORDERABLE') {
          errorMessage = this.pageLabels.ER130 || get(error, 'errorMessage');
        }
        if (get(error, 'errorKey') === 'NOT_AUTHORIZED_FOR_QUERY') {
          errorMessage = this.pageLabels.ER96 || get(error, 'errorMessage');
        }
        this.notification.show(errorMessage, 'error');
      }
    });
    if (isEmpty(errorMessage)) {
      errorMessage = this.pageLabels.ER71;
      this.notification.show(errorMessage, 'error');
    }
    return errorInvForAddon;
  }
  findWarningErrorMessages(messages) {
    if (!messages) {
      return;
    }
    forEach(messages, message => {
      const isWarning = message.severity === 'WARNING' && message.key !== '_ERR_UEP_QTY_MISMATCH';
      const isError = message.severity === 'ERROR';
      if (!message.message || !isWarning && !isError) {
        return;
      }
      this.notification.show(message.message, isWarning ? 'warning' : 'error');
      return;
    });
  }
  checkAllocationError(messages) {
    if (!messages || !messages.length) {
      return false;
    } else {
      const allocationError = messages.filter(item => {
        return item.key == '_ERR_INV_ALLOCATION';
      });
      return allocationError.length ? true : false;
    }
  }
  getItemsToUpdateDeliveryMethod(deliveryId) {
    const updatedItems = {
      orderItem: []
    };
    let errorItems = false;
    forEach(this.tickets, ticketGroup => {
      forEach(ticketGroup, ticket => {
        if (ticket.adultQuantity > 0) {
          forEach(ticket.ticketsForAdult.orderItems, item => {
            if (!this.checkAllocationError(item.messages)) {
              updatedItems.orderItem.push({
                orderItemId: item.orderItemId,
                quantity: Number(item.quantity),
                deliveryMethod: deliveryId
              });
            } else {
              errorItems = true;
            }
          });
        }
        if (ticket.childQuantity > 0) {
          forEach(ticket.ticketsForChild.orderItems, item => {
            if (!this.checkAllocationError(item.messages)) {
              updatedItems.orderItem.push({
                orderItemId: item.orderItemId,
                quantity: Number(item.quantity),
                deliveryMethod: deliveryId
              });
            } else {
              errorItems = true;
            }
          });
        }
        if (ticket.allQuantity > 0) {
          forEach(ticket.ticketsForAll.orderItems, item => {
            if (!this.checkAllocationError(item.messages)) {
              updatedItems.orderItem.push({
                orderItemId: item.orderItemId,
                quantity: Number(item.quantity),
                deliveryMethod: deliveryId
              });
            } else {
              errorItems = true;
            }
          });
        }
      });
    });
    if (errorItems) {
      throw false;
    }
    return updatedItems;
  }
  getCreateDate(ticket) {
    const ageGroupWithOrderItems = find(this.ages, age => {
      return get(ticket['ticketsFor' + age], 'orderItems.0.createDate');
    });
    if (!ageGroupWithOrderItems) {
      return;
    }
    return get(ticket, 'ticketsFor' + ageGroupWithOrderItems + '.orderItems.0.createDate');
  }
  itemsWithValidDeliveryMethod(orderItems, eligibleDeliveryMethods) {
    return filter$1(orderItems, orderItem => {
      const deliveryMethodId = this.getTicketDeliveryMethodId(orderItem);
      return some(eligibleDeliveryMethods, {
        id: deliveryMethodId
      });
    });
  }
  getTicketDeliveryMethodId(ticket) {
    const ageGroupWithOrderItems = find(this.ages, age => {
      return get(ticket['ticketsFor' + age], 'orderItems.0.shipping.shipModeId');
    });
    if (!ageGroupWithOrderItems) {
      return;
    }
    return get(ticket, 'ticketsFor' + ageGroupWithOrderItems + '.orderItems.0.shipping.shipModeId');
  }
  reorderGroups() {
    const ticketsGroups = {};
    ticketsGroups[this.defaultValues.PARK_TICKETS] = this.tickets[this.defaultValues.PARK_TICKETS];
    ticketsGroups[this.defaultValues.ANNUAL_PASSES] = this.tickets[this.defaultValues.ANNUAL_PASSES];
    ticketsGroups[this.defaultValues.EXPRESS_PASSES] = this.tickets[this.defaultValues.EXPRESS_PASSES];
    ticketsGroups[this.defaultValues.ADDONS] = this.tickets[this.defaultValues.ADDONS];
    this.tickets = ticketsGroups;
  }
  updateCartDetails(cartTicket) {
    return this.httpRequest(relativeUrls.UPDATE_TICKET, 'PUT', cartTicket);
  }
  checkForCartPage() {
    const path = this.window.location.href;
    const check = path.match(/(shopping-cart|payment-options|checkout-login)/gi);
    return check !== null ? true : false;
  }
  ngOnDestroy() {
    this.subscriptions$?.unsubscribe();
  }
  static {
    this.ɵfac = function CartService_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || CartService)(i0.ɵɵinject(WINDOW), i0.ɵɵinject('DefaultValues'), i0.ɵɵinject(i1.DalService), i0.ɵɵinject(i2.LocalStorageService), i0.ɵɵinject(i3.MsGuestsService), i0.ɵɵinject(i2.SessionStorageService), i0.ɵɵinject(i4.NotificationService), i0.ɵɵinject(i5.PageLabelsResolver));
    };
  }
  static {
    this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
      token: CartService,
      factory: CartService.ɵfac,
      providedIn: 'root'
    });
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(CartService, [{
    type: Injectable,
    args: [{
      providedIn: 'root'
    }]
  }], () => [{
    type: undefined,
    decorators: [{
      type: Inject,
      args: [WINDOW]
    }]
  }, {
    type: undefined,
    decorators: [{
      type: Inject,
      args: ['DefaultValues']
    }]
  }, {
    type: i1.DalService
  }, {
    type: i2.LocalStorageService
  }, {
    type: i3.MsGuestsService
  }, {
    type: i2.SessionStorageService
  }, {
    type: i4.NotificationService
  }, {
    type: i5.PageLabelsResolver
  }], null);
})();
class PayService extends BaseCartService {
  constructor(defaultValues, dal, localStorage, guest, sessionStorage) {
    super(defaultValues, dal, localStorage, guest);
    this.defaultValues = defaultValues;
    this.dal = dal;
    this.localStorage = localStorage;
    this.guest = guest;
    this.sessionStorage = sessionStorage;
  }
  getCardinalToken(orderId, timeout) {
    if (!orderId) {
      return;
    }
    const request = {
      orderId: orderId
    };
    return this.httpRequest(relativeUrls.CARDINAL_TOKEN, 'POST', request, undefined, timeout);
  }
  replaceURITokens(url, tokenValuePairs) {
    return tokenValuePairs.reduce((transformedUrl, tokenValuePair) => {
      const [token, value] = tokenValuePair;
      return transformedUrl.replace(token, value);
    }, url);
  }
  payCard(paymentInfo, cardType) {
    const secureGatewayEnabled = get(this.defaultValues, 'SECURE_API_ENABLED', 'true') === 'true';
    const secureBaseUrl = get(this.defaultValues, 'SECURE_API_BASEURL', '');
    if (cardType && cardType === CardTypes.VISA_COBRAND && paymentInfo?.customer_session_id) {
      return this.payFnboCard(paymentInfo, cardType, secureGatewayEnabled, secureBaseUrl);
    } else {
      const url = this.buildPaymentUrl(paymentInfo, secureGatewayEnabled, secureBaseUrl);
      const instanceId = this.guest.helper.getInstanceId() || '';
      const httpHeaders = this.buildHttpHeaders(instanceId);
      return this.httpRequest(url, 'POST', paymentInfo, httpHeaders, undefined, undefined, undefined, !!secureGatewayEnabled && !!secureBaseUrl ? secureBaseUrl : undefined).then(response => {
        this.sessionStorage.setItem('PAYMENT_RESPONSE', JSON.stringify(response.result));
        if (response?.statusCode !== 201) {
          throw response;
        }
        return response;
      });
    }
  }
  payFnboCard(paymentInfo, cardType, secureGatewayEnabled, secureBaseUrl) {
    const instanceId = this.guest.helper.getInstanceId();
    const guestId = this.guest.helper.getGuestId();
    const isGuestLoggedIn = this.guest.helper.isLoggedIn();
    const resortAreaCode = this.defaultValues.RESORT_AREA_CODE || this.defaultValues.PERSONALIZE_RESORT_CODE || 'UOR';
    let url = '';
    if (cardType && cardType === CardTypes.VISA_COBRAND && instanceId && !isGuestLoggedIn) {
      url = this.replaceInstanceIdPlaceholder();
    } else if (cardType && cardType === CardTypes.VISA_COBRAND && guestId && isGuestLoggedIn) {
      url = this.replaceGuestIdPlaceholder();
    }
    const httpHeaders = this.buildHttpHeaders(instanceId, resortAreaCode, true);
    return this.httpRequest(url, 'POST', paymentInfo, httpHeaders, undefined, true, undefined, !!secureGatewayEnabled && !!secureBaseUrl ? secureBaseUrl : undefined).then(response => {
      this.sessionStorage.setItem('PAYMENT_RESPONSE', JSON.stringify(response));
      if (response?.status !== 'APPROVE') {
        throw response;
      }
      return response;
    }).catch(error => {
      console.log(error);
      throw error;
    });
  }
  // Helper methods
  buildPaymentUrl(paymentInfo, secureGatewayEnabled, secureBaseUrl) {
    const isSecureEnabledAndBaseUrlAvailable = secureGatewayEnabled && secureBaseUrl;
    const url = paymentInfo.cardId ? relativeUrls.ADD_FOLIO_CARD : relativeUrls.PAYMENT_SUBMIT;
    const secureUrl = paymentInfo.cardId ? relativeUrls.PAY_ADD_FOLIO_CARD : relativeUrls.PAY_PAYMENT_SUBMIT;
    return isSecureEnabledAndBaseUrlAvailable ? url : secureUrl;
  }
  buildHttpHeaders(instanceId, resortAreaCode, sourceId) {
    let httpHeaders = new HttpHeaders();
    httpHeaders = httpHeaders.append('x-instance-id', instanceId);
    if (resortAreaCode) {
      httpHeaders = httpHeaders.append('x-resort-area-code', resortAreaCode);
    }
    if (sourceId) {
      httpHeaders = httpHeaders.append('x-source-Id', this.guest.helper.getSourceId('WebCard'));
    }
    return httpHeaders;
  }
  replaceInstanceIdPlaceholder() {
    return this.replaceURITokens(relativeUrls.PAYMENT_INSTANCE_SUBMIT || '/instances/{instance-id}/commerce-checkout-instant-card', [['{instance-id}', this.guest.helper.getInstanceId() || '']]);
  }
  replaceGuestIdPlaceholder() {
    return this.replaceURITokens(relativeUrls.PAYMENT_GUEST_SUBMIT || '/guests/{guest-id}/commerce-checkout-instant-card', [['{guest-id}', this.guest.helper.getGuestId() || '']]);
  }
  static {
    this.ɵfac = function PayService_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || PayService)(i0.ɵɵinject('DefaultValues'), i0.ɵɵinject(i1.DalService), i0.ɵɵinject(i2.LocalStorageService), i0.ɵɵinject(i3.MsGuestsService), i0.ɵɵinject(i2.SessionStorageService));
    };
  }
  static {
    this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
      token: PayService,
      factory: PayService.ɵfac,
      providedIn: 'root'
    });
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(PayService, [{
    type: Injectable,
    args: [{
      providedIn: 'root'
    }]
  }], () => [{
    type: undefined,
    decorators: [{
      type: Inject,
      args: ['DefaultValues']
    }]
  }, {
    type: i1.DalService
  }, {
    type: i2.LocalStorageService
  }, {
    type: i3.MsGuestsService
  }, {
    type: i2.SessionStorageService
  }], null);
})();
class PricingInventoryService extends BaseCartService {
  constructor(relativeUrls, defaultValues, dal, localStorage, guest, cacheFactory, spinner) {
    super(defaultValues, dal, localStorage, guest);
    this.relativeUrls = relativeUrls;
    this.defaultValues = defaultValues;
    this.dal = dal;
    this.localStorage = localStorage;
    this.guest = guest;
    this.cacheFactory = cacheFactory;
    this.spinner = spinner;
  }
  async loadInventoryByPartNumbers(startDate, endDate, partNumbersInfo, wheelChairs, cacheable) {
    if (moment(startDate) > moment(endDate)) {
      return Promise.resolve();
    }
    this.spinner.show();
    const monthCache = this.cacheFactory.exists('lruMonthCache') ? this.cacheFactory.get('lruMonthCache') : this.cacheFactory.createCache('lruMonthCache');
    let allPartNumbers = '';
    const data = {
      contractId: this.localStorage.getItem('CONTRACT_ID'),
      currency: 'USD',
      events: []
    };
    data.events = map(partNumbersInfo, currentPartNumber => {
      allPartNumbers += currentPartNumber.partNumber + currentPartNumber.quantity.toString();
      const event = {
        partNumber: currentPartNumber.partNumber,
        startDate: startDate,
        endDate: endDate,
        quantity: currentPartNumber.quantity.toString()
      };
      if (wheelChairs) {
        const iswhellChair = wheelChairs ? 'Y' : 'N';
        event.attributes = [{
          name: 'ADA',
          value: iswhellChair
        }];
      }
      return event;
    });
    let keyCachedMonth = startDate + ' ' + endDate + ' ' + allPartNumbers;
    if (wheelChairs) {
      keyCachedMonth += wheelChairs;
    }
    const cachedData = monthCache.get(keyCachedMonth),
      isCached = !!cachedData && cacheable;
    return Promise.resolve().then(() => {
      if (isCached) {
        this.spinner.hide();
        return cachedData;
      }
      return this.httpRequest(this.relativeUrls.PRICING_INVENTORY, 'POST', data);
    }).then(response => {
      if (isCached) {
        return cachedData;
      }
      this.spinner.hide();
      const eventAvailability = get(response, 'eventAvailability');
      monthCache.put(keyCachedMonth, eventAvailability);
      return eventAvailability;
    }).catch(error => {
      this.spinner.hide();
      throw error;
    });
  }
  destroyCache() {
    const monthCache = this.cacheFactory.exists('lruMonthCache') ? this.cacheFactory.get('lruMonthCache') : this.cacheFactory.createCache('lruMonthCache');
    if (monthCache && monthCache.info.length > 0) {
      monthCache.removeAll();
    }
  }
  loadSeasonalCalendarDatesComsAsync(startDate, endDate) {
    if (moment(startDate) > moment(endDate)) {
      return Promise.resolve();
    }
    this.spinner.show();
    const monthCache = this.cacheFactory.exists('lruSeasonalMonthCache') ? this.cacheFactory.get('lruSeasonalMonthCache') : this.cacheFactory.createCache('lruSeasonalMonthCache');
    const start = startDate,
      end = endDate;
    const calendarType = 'MONTHLY';
    const keyCachedMonth = join([start, end], ' ');
    const url = this.relativeUrls.SEASONAL_CALENDAR_DATES + start + '/' + end;
    const isCached = !!monthCache.get(keyCachedMonth);
    return Promise.resolve().then(() => {
      if (!isCached) {
        return this.httpRequest(url, 'GET', null);
      }
      this.spinner.hide();
      return monthCache.get(keyCachedMonth);
    }).then(response => {
      const seasonalValuesMonth = isCached ? response : get(response, 'result');
      const seasonalValues = calendarType === 'MONTHLY' ? seasonalValuesMonth : this.getSeasonalValuesWeek(startDate, seasonalValuesMonth, endDate);
      if (!isCached) {
        monthCache.put(keyCachedMonth, seasonalValuesMonth);
      }
      this.spinner.hide();
      return {
        data: seasonalValues,
        calendarType: calendarType
      };
    }).catch(error => {
      this.spinner.hide();
      throw {
        error: error,
        calendarType: calendarType
      };
    });
  }
  getSeasonalValuesWeek(startWeek, monthValues, endWeek) {
    const seasonalValuesWeek = {};
    Object.keys(monthValues).forEach(key => {
      if (moment(key).isBetween(startWeek, endWeek, undefined, '[]')) {
        seasonalValuesWeek[key] = monthValues[key];
      }
    });
    return seasonalValuesWeek;
  }
  static {
    this.ɵfac = function PricingInventoryService_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || PricingInventoryService)(i0.ɵɵinject('RelativeUrls'), i0.ɵɵinject('DefaultValues'), i0.ɵɵinject(i1.DalService), i0.ɵɵinject(i2.LocalStorageService), i0.ɵɵinject(i3.MsGuestsService), i0.ɵɵinject(i4$1.CacheFactory), i0.ɵɵinject(i5$1.SpinnerService));
    };
  }
  static {
    this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
      token: PricingInventoryService,
      factory: PricingInventoryService.ɵfac,
      providedIn: 'root'
    });
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(PricingInventoryService, [{
    type: Injectable,
    args: [{
      providedIn: 'root'
    }]
  }], () => [{
    type: undefined,
    decorators: [{
      type: Inject,
      args: ['RelativeUrls']
    }]
  }, {
    type: undefined,
    decorators: [{
      type: Inject,
      args: ['DefaultValues']
    }]
  }, {
    type: i1.DalService
  }, {
    type: i2.LocalStorageService
  }, {
    type: i3.MsGuestsService
  }, {
    type: i4$1.CacheFactory
  }, {
    type: i5$1.SpinnerService
  }], null);
})();

/*
 * Public API Surface of wcs-shop
 */

/**
 * Generated bundle index. Do not edit.
 */

export { CartService, PayService, PricingInventoryService, relativeUrls };
